import { Resources } from 'constants/resources';

import { blue, green, orange, yellow } from '@mui/material/colors';
import { useCreatePathWithQuery } from 'hooks/useCreatePathWithQuery';
import { ExpenseTableRow } from 'pages/Finance/ExpenseReport/ExpenseReportList/utils/buildExpenseReportTableRows';
import { Link, RecordContextProvider } from 'react-admin';
import { Typography } from 'shared/mui/Typography';
import { DatagridConfigurableProps } from 'shared/react-admin/Datagrids';
import {
  CurrencyField,
  EmbeddedFunctionField,
  FunctionField,
  UserProfileField,
} from 'shared/react-admin/Fields';
import { CurrencyAmountField } from 'shared/react-admin/Fields/CurrencyAmountField/CurrencyAmountField';
import { User } from 'types/embedded/user';
import { convertReactAdminIdToHydraId } from 'utils/convertReactAdminIdToHydraId';

import { DatagridConfigurableStyled } from './ExpenseReportListDatagrid.styled';

const getRowStyle = (record: ExpenseTableRow) => {
  switch (record.rowType) {
    case 'subarticleTotal':
      return { backgroundColor: blue[50] };
    case 'articleTotal':
      return { backgroundColor: green[50] };
    case 'referralTotal':
      return { backgroundColor: yellow[50] };
    case 'grandTotal':
      return { backgroundColor: orange[50] };
    default:
      return {};
  }
};

type ExpenseReportListDatagridProps = Pick<DatagridConfigurableProps, 'isPending'>;

export const ExpenseReportListDatagrid = (props: ExpenseReportListDatagridProps) => {
  const { createPathWithQuery } = useCreatePathWithQuery();

  return (
    <DatagridConfigurableStyled
      bulkActionButtons={false}
      isPending={props.isPending}
      resizableColumns={['date', 'referral']}
      rowStyle={getRowStyle}
    >
      <FunctionField
        cellClassName="column-date"
        label="Дата"
        render={(record: ExpenseTableRow) => {
          if (record.rowType === 'transaction' && record.date) {
            return new Date(record.date).toLocaleDateString('ru-RU');
          }

          const labels: Record<string, string> = {
            subarticleTotal: 'Итог по подстатье',
            articleTotal: 'Итог по статье',
            referralTotal: 'Итог по рефералу',
            grandTotal: 'Общий итог',
          };

          const label = labels[record.rowType];

          if (!label) {
            return '';
          }

          return (
            <Typography component="span" fontWeight="500" variant="body2">
              {label}
            </Typography>
          );
        }}
        source="date"
      />
      <FunctionField
        cellClassName="column-referral"
        label="Реферал"
        render={(record: ExpenseTableRow) => (
          <Typography
            component="span"
            fontWeight={record.rowType !== 'transaction' ? 500 : 400}
            variant="body2"
          >
            {record.referralName}
          </Typography>
        )}
        source="referral"
      />
      <FunctionField
        cellClassName="column-expenseId"
        label="ID расхода"
        render={(record: ExpenseTableRow) => {
          if (record.rowType === 'transaction' && record.expenseId) {
            return (
              <Link
                target="_blank"
                to={createPathWithQuery({
                  resource: Resources.BankOperations,
                  id: convertReactAdminIdToHydraId(
                    record?.expenseId,
                    Resources.RequestBankOperations,
                  ),
                  type: 'show',
                })}
              >
                {record.expenseId}
              </Link>
            );
          }
          return '';
        }}
      />
      <FunctionField
        cellClassName="column-expenseType"
        label="Тип расхода"
        render={(record: ExpenseTableRow) => record.expenseType || ''}
      />
      <FunctionField
        cellClassName="column-articleName"
        label="Статья затрат"
        render={(record: ExpenseTableRow) => (
          <Typography
            component="span"
            fontWeight={record.rowType !== 'transaction' ? 500 : 400}
            variant="body2"
          >
            {record.articleName}
          </Typography>
        )}
      />
      <FunctionField
        cellClassName="column-subarticleName"
        label="Подстатья затрат"
        render={(record: ExpenseTableRow) => (
          <Typography
            component="span"
            fontWeight={record.rowType !== 'transaction' ? 500 : 400}
            variant="body2"
          >
            {record.subarticleName}
          </Typography>
        )}
      />
      <FunctionField
        cellClassName="column-merchantAccountAmount"
        headerClassName="column-merchantAccountAmount"
        label="Сумма расхода в валюте счета мерчанта"
        render={(record: ExpenseTableRow) => {
          if (record.rowType === 'transaction' && record.merchantAccountAmount) {
            return (
              <RecordContextProvider value={{ amount: record.merchantAccountAmount }}>
                <CurrencyAmountField fontWeight={400} source="amount" />
              </RecordContextProvider>
            );
          }
          return '';
        }}
      />
      <FunctionField
        cellClassName="column-merchantAccountCurrency"
        label="Валюта счета мерчанта"
        render={(record: ExpenseTableRow) => {
          if (record.rowType === 'transaction' && record.merchantAccountCurrency) {
            return (
              <RecordContextProvider
                value={{
                  alphaCode: record.merchantAccountCurrency,
                  name: record.merchantAccountCurrencyName,
                }}
              >
                <CurrencyField alphaCodeSource="alphaCode" nameSource="name" />
              </RecordContextProvider>
            );
          }
          return '';
        }}
      />
      <FunctionField
        cellClassName="column-selectedCurrencyAmount"
        headerClassName="column-selectedCurrencyAmount"
        label="Сумма расхода в выбранной валюте"
        render={(record: ExpenseTableRow) => {
          const amount =
            record.rowType === 'transaction' ? record.selectedCurrencyAmount : record.totalAmount;
          const fontWeight = record.rowType !== 'transaction' ? 500 : 400;

          if (amount) {
            return (
              <RecordContextProvider value={{ amount }}>
                <CurrencyAmountField fontWeight={fontWeight} source="amount" />
              </RecordContextProvider>
            );
          }
          return '';
        }}
        textAlign="right"
      />
      <FunctionField
        cellClassName="column-selectedCurrency"
        label="Выбранная валюта"
        render={(record: ExpenseTableRow) => {
          const currencyCode =
            record.rowType === 'transaction' ? record.selectedCurrency : record.currencyCode;
          const currencyName =
            record.rowType === 'transaction' ? record.selectedCurrencyName : record.currencyName;

          if (currencyCode) {
            return (
              <RecordContextProvider
                value={{
                  alphaCode: currencyCode,
                  name: currencyName || currencyCode,
                }}
              >
                <CurrencyField alphaCodeSource="alphaCode" nameSource="name" />
              </RecordContextProvider>
            );
          }
          return '';
        }}
      />
      <FunctionField
        cellClassName="column-comment"
        label="Комментарий"
        render={(record: ExpenseTableRow) => record.comment || ''}
      />
      <EmbeddedFunctionField
        label="Согласующий / Аннулирующий"
        render={(value: Partial<User>) => {
          return (
            <RecordContextProvider value={value}>
              <UserProfileField />
            </RecordContextProvider>
          );
        }}
        sourceField="approver"
      />
    </DatagridConfigurableStyled>
  );
};
